// src/i18n.ts

import bg from '@/locales/bg.json';
import da_DK from '@/locales/da_DK.json';
import de from '@/locales/de.json';
import de_AT from '@/locales/de_AT.json';
import el_GR from '@/locales/el_GR.json';
import en from '@/locales/en.json';
import es from '@/locales/es.json';
import fr_CA from '@/locales/fr_CA.json';
import fr_FR from '@/locales/fr_FR.json';
import it_IT from '@/locales/it_IT.json';
import ja from '@/locales/ja.json';
import ko from '@/locales/ko.json';
import mi_NZ from '@/locales/mi_NZ.json';
import nl from '@/locales/nl.json';
import pl from '@/locales/pl.json';
import pt_BR from '@/locales/pt_BR.json';
import sv_SE from '@/locales/sv_SE.json';
import tr from '@/locales/tr.json';
import uk from '@/locales/uk.json';
import { type Locale } from '@/schemas/i18n/locale';
import { WindowService } from '@/services/window.service';
import { createI18n, type Composer } from 'vue-i18n';

/**
 * Internationalization configuration and utilities.
 * Sets up Vue i18n instance with locale management and message loading.
 */

type MessageSchema = typeof en;
type GlobalComposer = Composer<{}, {}, {}, Locale>;

/**
 * The list of supported locales comes directly from etc/config.yaml.
 */
const domainBranding = WindowService.get('domain_branding');
const supportedLocales = WindowService.get('supported_locales') || [];
const fallbackLocale = WindowService.get('fallback_locale') || {};
const defaultLocale = WindowService.get('default_locale') || 'en';
const displayLocale = domainBranding?.locale ?? WindowService.get('locale');

/**
 * Creates a completely independent i18n instance with its own locale state and message
 * store. We eat this dogfood below to create the global i18n instance as well.
 *
 * This differs from useI18n({ useScope: 'local' }) which only provides component-level
 * message isolation within the global instance. Local scope - still uses global
 * instance but with component-isolated messages. When the global locale changes, the
 * local scope will update accordingly.
 *
 * Use createI18nInstance when you need:
 *
 * - A fully isolated instance that won't affect or be affected by the global app locale
 * - Independent message loading and locale switching
 * - Preview/sandbox functionality that should remain separate from the main app
 * - Testing scenarios where global state isolation is required
 *
 * @param initialLocale - Initial locale to use for this instance
 * @returns Object containing:
 *   - instance (I18n): The raw i18n instance.
 *   - composer (Composer): The composer for accessing translations.
 *   - setLocale (Locale): Function to change locale for this instance only.
 */
export function createI18nInstance(initialLocale: string = defaultLocale) {
  const instance = createI18n<false>({
    legacy: false, // Enable composition API.
    locale: initialLocale,
    fallbackLocale: fallbackLocale,
    globalInjection: true, // allows $t to be used globally.
    missingWarn: true, // these enable browser console logging
    fallbackWarn: true, // and are removed from prod builds.
    messages: {
      bg,
      da_DK,
      de,
      de_AT,
      el_GR,
      en, // Always include default messages
      es,
      fr_CA,
      fr_FR,
      it_IT,
      ja,
      ko,
      mi_NZ,
      nl,
      pl,
      pt_BR,
      sv_SE,
      tr,
      uk,
    },
    availableLocales: supportedLocales,
  });

  /**
   * Access the root Composer for this i18n instance.
   *
   * Vue I18n has a hierarchical structure:
   * - instance.global: The root Composer handling translations at instance level
   * - useI18n(): Component-level Composer that inherits from instance.global
   * - useI18n({ useScope: 'local' }): Isolated component-level Composer
   *
   * Even for non-global instances (like preview instances), we still access
   * the root Composer via .global since it represents the root scope of that
   * specific instance.
   *
   * @see https://vue-i18n.intlify.dev/guide/advanced/scope.html
   */
  const composer = instance.global as GlobalComposer;

  /**
   * Updates locale for this instance only
   * @param locale - Target locale to set
   */
  const setLocale = async (locale: string) => {
    if (!composer.availableLocales.includes(locale)) {
      const messages = await loadLocaleMessages(locale);
      if (messages) {
        composer.setLocaleMessage(locale, messages);
      }
    }
    composer.locale.value = locale;
  };

  return {
    instance,
    composer,
    setLocale,
  };
}

/** Create and export the global instance */
const {
  instance: i18n,
  composer: globalComposer,
  setLocale: setGlobalLocale,
} = createI18nInstance(displayLocale);
export default i18n;
export { globalComposer, setGlobalLocale };

/**
 * Dynamically imports locale message files.
 * @param locale - Locale code to load (e.g. 'en', 'es')
 * @returns Loaded messages or null if failed
 */
async function loadLocaleMessages(locale: string): Promise<MessageSchema | null> {
  console.debug(`Attempting to load locale: ${locale}`);
  try {
    const messages = await import(`@/locales/${locale}.json`);
    console.debug(`Successfully loaded locale: ${locale}`);
    return messages.default;
  } catch (error) {
    console.error(`Failed to load locale: ${locale}`, error);
    return null;
  }
}

/**
 * Flattens nested message structure while maintaining backwards compatibility.
 * Supports both flat and dot-notation key access.
 * @param messages - Nested message object
 * @returns Flattened messages with preserved key paths
 */
export function createCompatibilityLayer(messages: Record<string, any>): Record<string, string> {
  const flat: Record<string, string> = {};

  /**
   * Recursively flattens object into dot notation paths.
   * @param obj - Source object
   * @param prefix - Current key path
   */
  function flattenKeys(obj: Record<string, unknown>, prefix = ''): void {
    Object.entries(obj).forEach(([key, value]) => {
      if (value && typeof value === 'object') {
        flattenKeys(value as Record<string, unknown>, `${prefix}${key}.`);
      } else {
        flat[key] = value as string; // Maintain old keys
        flat[`${prefix}${key}`] = value as string; // Add new nested keys
      }
    });
  }

  flattenKeys(messages);
  return flat;
}
